import React, { useState, useEffect } from 'react';
import { Download, Search, FileSpreadsheet } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { formatDate, todayFormatted } from '../../utils/dateUtils';
import { useLoader } from '../../components/shared/LoaderContext';

export default function AdminReportsPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState(null);
  const { showLoader, hideLoader } = useLoader();

  const STATUS_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'REFERRAL_PENDING', label: 'Referral Pending' },
    { value: 'REFERRAL_APPROVED', label: 'Referral Approved' },
    { value: 'PAYMENT_RECEIVED', label: 'Payment Received' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'REJECTED', label: 'Rejected' },
  ];

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 1000 });
      if (statusFilter) params.append('status', statusFilter);
      if (search) params.append('search', search);
      const [membersRes, statsRes] = await Promise.all([
        api.get(`/admin/members?${params}`),
        api.get('/admin/reports'),
      ]);
      let data = membersRes.data.members;
      if (fromDate) data = data.filter((m) => new Date(m.createdAt) >= new Date(fromDate));
      if (toDate) data = data.filter((m) => new Date(m.createdAt) <= new Date(toDate + 'T23:59:59'));
      setMembers(data);
      setStats(statsRes.data.stats);
    } catch {
      toast.error('Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, []);

  const handleGenerate = async () => {
    const interval = showLoader('Generating report...');
    await fetchReport();
    hideLoader(interval);
  };

  // ── CSV Export — ALL fields matching Report.xlsx format ──
  const exportToCSV = () => {
    if (members.length === 0) { toast.error('No data to export'); return; }

    const today = todayFormatted();
    const fromLabel = fromDate ? formatDate(fromDate) : 'All';
    const toLabel = toDate ? formatDate(toDate) : 'All';

    // Header rows matching Report.xlsx
    const headerRows = [
      ['', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', 'Bombay Psychiatric Society'],
      ['', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', 'Membership Report'],
      ['', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', `Date: ${today}`],
      ['', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', `From Date: ${fromLabel}   To  Date: ${toLabel}`],
      [],
      [
        'Sr.no', 'Member ID', 'Full Name', 'Email', 'Mobile', 'Registered On',
        'Date of Birth', 'Age', 'Gender', 'Nationality',
        'Residential Address', 'City', 'State', 'Pin Code',
        'Degree', 'University', 'Year of Passing',
        'Areas of Expertise', 'Clinic/Practice Address', 'Membership Type',
        'Referral 1 Name', 'Referral 1 Approval Date',
        'Referral 2 Name', 'Referral 2 Approval Date',
        'Amount Received', 'Payment Date', 'Mode of Payment',
        'Reference Number', 'Remarks', 'Status'
      ]
    ];

    const dataRows = members.map((m, idx) => {
      const ref1 = m.referralApprovals?.find((a) => a.referralNumber === 1);
      const ref2 = m.referralApprovals?.find((a) => a.referralNumber === 2);
      const fullName = `${m.user.firstName} ${m.user.middleName || ''} ${m.user.lastName}`.trim();
      const ref1Name = ref1?.referralUser ? `${ref1.referralUser.firstName} ${ref1.referralUser.lastName}` : '';
      const ref2Name = ref2?.referralUser ? `${ref2.referralUser.firstName} ${ref2.referralUser.lastName}` : '';

      return [
        idx + 1,
        m.memberId,
        fullName,
        m.user.email,
        m.user.mobile,
        formatDate(m.createdAt),           // DD/MM/YYYY
        formatDate(m.dateOfBirth),          // DD/MM/YYYY
        m.age || '',
        m.gender || '',
        m.nationality || '',
        m.address || '',
        m.city || '',
        m.state || '',
        m.pincode || '',
        m.degree || '',
        m.university || '',
        m.yearOfPassing || '',
        m.areasOfInterest || '',
        m.officeAddress || '',
        m.membershipType || '',
        ref1Name,
        formatDate(ref1?.approvedAt),       // DD/MM/YYYY
        ref2Name,
        formatDate(ref2?.approvedAt),       // DD/MM/YYYY
        m.payment?.receivedAmount || '',
        formatDate(m.payment?.paymentDate), // DD/MM/YYYY
        m.payment?.modeOfPayment || '',
        m.payment?.referenceNumber || '',
        m.payment?.remarks || '',
        m.status.replace(/_/g, ' '),
      ];
    });

    const allRows = [...headerRows, ...dataRows];
    const csvContent = allRows.map((row) =>
      row.map((cell) => `"${String(cell === '—' ? '' : cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `BPS_Membership_Report_${today.replace(/\//g, '-')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Report exported with ${members.length} records!`);
  };

  const statusBadge = {
    PENDING: 'pending', REFERRAL_PENDING: 'pending',
    REFERRAL_APPROVED: 'approved', PAYMENT_PENDING: 'pending',
    PAYMENT_RECEIVED: 'payment', ACTIVE: 'active', REJECTED: 'rejected',
  };

  return (
    <div>
      <div className="page-header">
        <h2>Membership Report</h2>
        <p>Bombay Psychiatric Society — Member Registration Report</p>
      </div>

      {/* Stats Cards — responsive auto-fit grid */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 16, marginBottom: 24
        }}>
          {[
            { label: 'Total Registrations', value: stats.totalRegistrations, color: 'var(--primary)' },
            { label: 'Active Members', value: stats.activeMembers, color: 'var(--success)' },
            { label: 'Pending Approvals', value: stats.pendingReferral, color: '#d97706' },
            { label: 'Total Collection', value: `₹${Number(stats.totalPaymentAmount || 0).toLocaleString('en-IN')}`, color: '#7c3aed' },
          ].map((s) => (
            <div key={s.label} className="stat-card">
              <div className="stat-value" style={{ color: s.color, fontSize: 24 }}>{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters — wraps naturally on small screens */}
      <div className="card" style={{ padding: '16px', marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ flex: '1 1 140px', minWidth: 140 }}>
            <label className="form-label">From Date</label>
            <input type="date" className="form-input"
              value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            {fromDate && (
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatDate(fromDate)}</span>
            )}
          </div>
          <div className="form-group" style={{ flex: '1 1 140px', minWidth: 140 }}>
            <label className="form-label">To Date</label>
            <input type="date" className="form-input"
              value={toDate} onChange={(e) => setToDate(e.target.value)} />
            {toDate && (
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{formatDate(toDate)}</span>
            )}
          </div>
          <div className="form-group" style={{ flex: '1 1 160px', minWidth: 160 }}>
            <label className="form-label">Status</label>
            <select className="form-input" value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}>
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ flex: '2 1 200px', minWidth: 180 }}>
            <label className="form-label">Search</label>
            <input className="form-input" placeholder="Name, email or Member ID..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleGenerate()} />
          </div>
          <button className="btn btn-primary" onClick={handleGenerate} style={{ marginBottom: 1 }}>
            <Search size={14} /> Generate
          </button>
          <button className="btn" onClick={exportToCSV}
            style={{ background: '#16a34a', color: 'white', marginBottom: 1 }}>
            <Download size={14} /> Export CSV
          </button>
        </div>
        <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-muted)' }}>
          {members.length} record(s) found
          {(fromDate || toDate) && ` | ${formatDate(fromDate) || 'Start'} to ${formatDate(toDate) || 'Today'}`}
        </div>
      </div>

      {/* Report Table — Minimum essential columns only */}
      <div className="card">
        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : members.length === 0 ? (
          <div className="empty-state">
            <FileSpreadsheet size={40} />
            <p>No records found. Click Generate to load data.</p>
          </div>
        ) : (
          <>
            <div style={{
              padding: '10px 16px', background: '#eff6ff',
              borderBottom: '1px solid var(--border)',
              fontSize: 12, color: '#1e40af',
              display: 'flex', alignItems: 'center', gap: 8
            }}>
              <FileSpreadsheet size={14} />
              Table shows key fields only. Use <strong>Export CSV</strong> for the complete report with all fields.
            </div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Sr.</th>
                    <th>Member ID</th>
                    <th>Full Name</th>
                    <th>Mobile</th>
                    <th>Reg. Date</th>
                    <th>City</th>
                    <th>Referral 1</th>
                    <th>Referral 2</th>
                    <th>Amount (₹)</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m, idx) => {
                    const ref1 = m.referralApprovals?.find((a) => a.referralNumber === 1);
                    const ref2 = m.referralApprovals?.find((a) => a.referralNumber === 2);
                    const fullName = `${m.user.firstName} ${m.user.middleName || ''} ${m.user.lastName}`.trim();
                    return (
                      <tr key={m.id}>
                        <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{idx + 1}</td>
                        <td style={{ fontWeight: 700, color: 'var(--primary)', fontFamily: 'monospace', fontSize: 12 }}>
                          {m.memberId}
                        </td>
                        <td style={{ fontWeight: 500, fontSize: 13 }}>{fullName}</td>
                        <td style={{ fontSize: 12 }}>{m.user.mobile}</td>
                        {/* DD/MM/YYYY format */}
                        <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatDate(m.createdAt)}</td>
                        <td style={{ fontSize: 12 }}>{m.city || '—'}</td>
                        <td style={{ fontSize: 12 }}>
                          {ref1?.referralUser
                            ? `${ref1.referralUser.firstName} ${ref1.referralUser.lastName}`
                            : '—'}
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {ref2?.referralUser
                            ? `${ref2.referralUser.firstName} ${ref2.referralUser.lastName}`
                            : '—'}
                        </td>
                        <td style={{ fontSize: 12, fontWeight: 600 }}>
                          {m.payment?.receivedAmount
                            ? `₹${Number(m.payment.receivedAmount).toLocaleString('en-IN')}`
                            : '—'}
                        </td>
                        <td>
                          <span className={`badge badge-${statusBadge[m.status] || 'pending'}`}
                            style={{ fontSize: 10 }}>
                            {m.status.replace(/_/g, ' ')}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
